// 帳號層級的身分（全站製作 / PM / 導演 / 動畫師）。前端和後台（cf-worker/worker.js）共用，改了要一起上線。
// 只能放純判斷：不 import 別的檔案、不碰 DOM / React、不用 enum。
//
// PM 帳號（0060 users.pm_account = 1）底層 role 維持 'animator'：所有權限判斷都不多給。
// 它只決定畫面上的稱呼、用戶頁的分組、加進專案時預設的專案角色；要在某個專案當動畫師，在那個專案單獨改。

export type AccountChoice = 'admin' | 'pm' | 'director' | 'animator';

/** 用戶頁分組的順序 */
export const ACCOUNT_CHOICE_ORDER: readonly AccountChoice[] = ['admin', 'pm', 'director', 'animator'];

export const ACCOUNT_CHOICE_LABEL: Record<AccountChoice, string> = {
  admin: '全站製作',
  pm: 'PM',
  director: '導演',
  animator: '動畫師',
};

export interface AccountLike {
  role: string;
  isPmAccount?: boolean;
}

/** 這個帳號在畫面上是哪一種（舊版後台沒有 isPmAccount = 不是 PM 帳號） */
export function accountChoiceOf(u: AccountLike): AccountChoice {
  if (u.role === 'admin') return 'admin';
  if (u.role === 'director') return 'director';
  return u.isPmAccount ? 'pm' : 'animator';
}

/** 帳號的顯示名稱 */
export function accountRoleLabel(u: AccountLike): string {
  return ACCOUNT_CHOICE_LABEL[accountChoiceOf(u)];
}

/** 選單選了某一種 → 要送給後台的兩個欄位 */
export function accountChoicePatch(choice: AccountChoice): { role: 'admin' | 'director' | 'animator'; pmAccount: boolean } {
  if (choice === 'pm') return { role: 'animator', pmAccount: true };
  return { role: choice, pmAccount: false };
}

/** 加進專案時預設的專案角色：PM 帳號 = PM（admin）；其他照帳號的角色 */
export function defaultProjectRole<R extends string>(u: { role: R; isPmAccount?: boolean }): R | 'admin' {
  return u.isPmAccount && u.role === 'animator' ? 'admin' : u.role;
}

const ACCOUNT_ROLES: readonly string[] = ['admin', 'animator', 'director'];
export const PM_ACCOUNT_BAD_ROLE = 'PM 帳號不能同時是全站製作或導演';
export const PM_ACCOUNT_BAD_VALUE = 'pmAccount 要是 true 或 false';

export type AccountFieldsPlan =
  | { ok: true; role?: string; pmAccount?: 0 | 1 }
  | { ok: false; status: 400; error: string };

/**
 * 全站製作建 / 改帳號：body 的 role、pmAccount → 要寫進資料庫的欄位（沒有的欄位 = 不動）。
 *   pmAccount: true  → role 一定是 animator（帶別的角色 = 400）
 *   pmAccount: false → 拿掉 PM 標記，role 照帶的
 *   沒帶 pmAccount（舊版前端）→ 改成別的角色會拿掉標記；帶 animator 或沒帶 role = 標記不動
 * 不認得的 role 跟以前一樣當作沒帶。
 */
export function planAccountFields(body: { role?: unknown; pmAccount?: unknown } | null | undefined): AccountFieldsPlan {
  const role = typeof body?.role === 'string' && ACCOUNT_ROLES.includes(body.role) ? body.role : undefined;
  const pm = body?.pmAccount;
  if (pm !== undefined && typeof pm !== 'boolean') return { ok: false, status: 400, error: PM_ACCOUNT_BAD_VALUE };
  if (pm === true) {
    if (role && role !== 'animator') return { ok: false, status: 400, error: PM_ACCOUNT_BAD_ROLE };
    return { ok: true, role: 'animator', pmAccount: 1 };
  }
  if (pm === false) return { ok: true, ...(role ? { role } : {}), pmAccount: 0 };
  if (role && role !== 'animator') return { ok: true, role, pmAccount: 0 };
  return { ok: true, ...(role ? { role } : {}) };
}
